import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const tiposCedula = [
  { value: '01', label: 'Física' },
  { value: '02', label: 'Jurídica' },
  { value: '03', label: 'DIMEX' },
  { value: '04', label: 'NITE' },
];

const CustomerInfoModal = ({ userId, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(true);
  const [savingFE, setSavingFE] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    lastName: '',
    address: '',
    phone: '',
    paymentMethod: '',
  });

  // FE
  const [requiereFE, setRequiereFE] = useState(false);
  const [feData, setFeData] = useState({
    tipo_cedula: '01',
    numero_cedula: '',
    razon_social: '',
    correo_facturacion: '',
    telefono_facturacion: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        if (!userId) return;
        const { data, error } = await supabase
          .from('clientes')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('Error cargando cliente:', error);
        } else if (data) {
          const {
            nombre, celular, provincia, canton, distrito, otras_senas,
            requiere_fe, tipo_cedula, numero_cedula, razon_social, correo_facturacion, telefono_facturacion,
          } = data;

          setCustomerInfo((prev) => ({
            ...prev,
            name: nombre || '',
            lastName: prev.lastName,
            phone: celular || '',
            address: [provincia, canton, distrito, otras_senas].filter(Boolean).join(', '),
          }));

          setRequiereFE(!!requiere_fe);
          setFeData({
            tipo_cedula: tipo_cedula || '01',
            numero_cedula: numero_cedula || '',
            razon_social: razon_social || '',
            correo_facturacion: correo_facturacion || '',
            telefono_facturacion: telefono_facturacion || '',
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((p) => ({ ...p, [name]: value }));
  };
  const handleFEChange = (e) => {
    const { name, value } = e.target;
    setFeData((p) => ({ ...p, [name]: value }));
  };

  const saveFEIfNeeded = async () => {
    setSavingFE(true);
    try {
      const payload = requiereFE
        ? {
            requiere_fe: true,
            tipo_cedula: feData.tipo_cedula || null,
            numero_cedula: feData.numero_cedula || null,
            razon_social: feData.razon_social || null,
            correo_facturacion: feData.correo_facturacion || null,
            telefono_facturacion: feData.telefono_facturacion || null,
          }
        : { requiere_fe: false };

      const { error } = await supabase
        .from('clientes')
        .update(payload)
        .eq('id', userId);

      if (error) {
        console.error('Error actualizando FE en clientes:', error);
        alert('No pudimos guardar los datos de Factura Electrónica.');
        return false;
      }
      return true;
    } finally {
      setSavingFE(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerInfo.name || !customerInfo.phone || !customerInfo.address || !customerInfo.paymentMethod) {
      alert('Por favor, completa nombre, teléfono, dirección y método de pago.');
      return;
    }
    if (requiereFE && (!feData.numero_cedula || !feData.correo_facturacion)) {
      alert('Para FE, completa el número de cédula y el correo de facturación.');
      return;
    }

    const ok = await saveFEIfNeeded();
    if (!ok) return;

    onConfirm({ ...customerInfo });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 p-4">
      {/* Contenedor ancho y con scroll interno */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-2xl font-bold text-gray-900">Confirmá tus datos</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body con scroll si es largo */}
        <div className="max-h-[80vh] overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Datos cliente: 2 columnas en desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                placeholder="Nombre"
                value={customerInfo.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5A61E5]"
                required
              />
              <input
                type="text"
                name="lastName"
                placeholder="Apellidos (opcional)"
                value={customerInfo.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5A61E5]"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Teléfono"
                value={customerInfo.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5A61E5]"
                required
              />
              <select
                name="paymentMethod"
                value={customerInfo.paymentMethod}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5A61E5]"
                required
              >
                <option value="">Método de pago</option>
                <option value="SINPE Móvil">SINPE Móvil</option>
                <option value="Transferencia bancaria">Transferencia bancaria</option>
                <option value="Pago con tarjeta">Pago con tarjeta</option>
                <option value="Emma Pay">Emma Pay</option>
              </select>

              {/* Dirección ocupa 2 columnas */}
              <textarea
                name="address"
                placeholder="Dirección de envío"
                value={customerInfo.address}
                onChange={handleInputChange}
                rows="3"
                className="md:col-span-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5A61E5] resize-none"
                required
              />
            </div>

            {/* FE */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiereFE}
                  onChange={(e) => setRequiereFE(e.target.checked)}
                  className="mt-1"
                />
                <div className="text-sm text-gray-800">
                  Necesito Factura Electrónica
                  <div className="text-xs text-gray-500">(Si estás registrado en Hacienda, marca esta casilla)</div>
                </div>
              </label>

              {requiereFE && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Tipo de cédula</label>
                    <select
                      name="tipo_cedula"
                      value={feData.tipo_cedula}
                      onChange={handleFEChange}
                      className="w-full border rounded-lg p-3 bg-white"
                    >
                      {tiposCedula.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Número de cédula</label>
                    <input
                      type="text"
                      name="numero_cedula"
                      value={feData.numero_cedula}
                      onChange={handleFEChange}
                      className="w-full border rounded-lg p-3"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Razón Social</label>
                    <input
                      type="text"
                      name="razon_social"
                      value={feData.razon_social}
                      onChange={handleFEChange}
                      className="w-full border rounded-lg p-3"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Correo de facturación</label>
                    <input
                      type="email"
                      name="correo_facturacion"
                      value={feData.correo_facturacion}
                      onChange={handleFEChange}
                      className="w-full border rounded-lg p-3"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Teléfono de facturación (opcional)</label>
                    <input
                      type="tel"
                      name="telefono_facturacion"
                      value={feData.telefono_facturacion}
                      onChange={handleFEChange}
                      className="w-full border rounded-lg p-3"
                    />
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer fijo */}
        <div className="px-6 py-4 border-t bg-white flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={savingFE}
            className="bg-[#F2BF53] text-gray-900 px-6 py-3 rounded-xl hover:bg-[#E0A840] transition-colors font-semibold"
          >
            {savingFE ? 'Guardando…' : 'Confirmar compra'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoModal;
